import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
	COLOR_MODES,
	HUE_PALETTES,
	MAP_PANEL_SUBTITLE,
	MAP_PANEL_TITLE,
	PRESET_OPTIONS,
	REGION_SUBSET_OPTIONS,
} from "./data";

const sectionTitleSx = {
	fontSize: 12,
	fontWeight: 700,
	letterSpacing: 0.8,
	textTransform: "uppercase",
	color: "#4d5c57",
};

const controlButtonSx = (active) => ({
	borderRadius: 999,
	textTransform: "none",
	fontWeight: 600,
	px: 1.5,
	borderColor: active ? "#22443a" : "#b9c7c2",
	background: active ? "#22443a" : "#fff",
	color: active ? "#fff" : "#22443a",
	"&:hover": {
		borderColor: "#22443a",
		background: active ? "#183129" : "#f2f7f5",
	},
});

const NONE_BRAND_SENTINEL = "__NONE__";

const MapControls = ({
	filterObj,
	setFilterObj,
	controlsOpen,
	stateOptions,
	layerOptions,
	summaryCards,
	competitorBrandOptions,
	onApplyFilters,
	isApplying,
	hasPendingChanges,
}) => {
	const visibleStates = stateOptions.filter((state) => state.toLowerCase().includes(filterObj.stateQuery.toLowerCase()));
	const visibleCompetitorBrands = competitorBrandOptions.filter((brand) =>
		brand.toLowerCase().includes(filterObj.competitorQuery.toLowerCase()),
	);
	const activeColorMode = COLOR_MODES.find((mode) => mode.key === filterObj.colorMode) ?? COLOR_MODES[0];
	const activePalette = HUE_PALETTES.find((palette) => palette.key === filterObj.palette) ?? HUE_PALETTES[0];
	const paletteGradient = `linear-gradient(to right, ${activePalette.colors.join(", ")})`;
	const hasStoreLayersEnabled =
		filterObj.layers.STORE_EBO_POINTS || filterObj.layers.STORE_MBO_POINTS || filterObj.layers.STORE_LFS_POINTS;
	const isAllCompetitorBrandsSelected = filterObj.selectedCompetitorBrands.length === 0;
	const isNoCompetitorBrandsSelected =
		filterObj.selectedCompetitorBrands.length === 1 && filterObj.selectedCompetitorBrands[0] === NONE_BRAND_SENTINEL;

	const setSelectedStates = (selectedStates) => {
		setFilterObj((prev) => ({ ...prev, selectedStates }));
	};

	const setSelectedCompetitorBrands = (selectedCompetitorBrands) => {
		setFilterObj((prev) => ({ ...prev, selectedCompetitorBrands }));
	};

	const handleStateToggle = (stateName) => {
		if (stateName === "All India") {
			setSelectedStates(["All India"]);
			return;
		}

		setFilterObj((prev) => {
			const current = prev.selectedStates.filter((state) => state !== "All India");
			const selectedStates = current.includes(stateName)
				? current.filter((state) => state !== stateName)
				: [...current, stateName];
			return { ...prev, selectedStates };
		});
	};

	const handleLayerToggle = (key) => {
		setFilterObj((prev) => ({
			...prev,
			layers: { ...prev.layers, [key]: !prev.layers[key] },
		}));
	};

	const applyPreset = (preset) => {
		let nextFilterObj = null;

		if (preset === "master") {
			nextFilterObj = {
				...filterObj,
				layers: {
					...filterObj.layers,
					STATE_BORDERS: true,
					MASTER_POLYGONS: true,
					ONLINE_POLYGONS: false,
					SKATER_POLYGONS: false,
					STORE_EBO_POINTS: false,
					STORE_MBO_POINTS: false,
					STORE_LFS_POINTS: false,
					COMPETITOR_POINTS: false,
				},
			};
			setFilterObj(nextFilterObj);
			onApplyFilters(nextFilterObj);
			return;
		}

		if (preset === "skater") {
			nextFilterObj = {
				...filterObj,
				layers: {
					...filterObj.layers,
					STATE_BORDERS: true,
					MASTER_POLYGONS: true,
					ONLINE_POLYGONS: false,
					SKATER_POLYGONS: true,
					STORE_EBO_POINTS: false,
					STORE_MBO_POINTS: false,
					STORE_LFS_POINTS: false,
					COMPETITOR_POINTS: false,
				},
			};
			setFilterObj(nextFilterObj);
			onApplyFilters(nextFilterObj);
			return;
		}

		nextFilterObj = {
			...filterObj,
			layers: {
				STATE_BORDERS: true,
				MASTER_POLYGONS: true,
				ONLINE_POLYGONS: true,
				SKATER_POLYGONS: true,
				STORE_EBO_POINTS: true,
				STORE_MBO_POINTS: true,
				STORE_LFS_POINTS: true,
				COMPETITOR_POINTS: true,
			},
		};
		setFilterObj(nextFilterObj);
		onApplyFilters(nextFilterObj);
	};

	const handleCompetitorBrandToggle = (brand) => {
		setFilterObj((prev) => {
			const current = prev.selectedCompetitorBrands.filter((item) => item !== NONE_BRAND_SENTINEL);
			if (!prev.selectedCompetitorBrands.length) {
				return {
					...prev,
					selectedCompetitorBrands: competitorBrandOptions.filter((option) => option !== brand),
				};
			}

			const selectedCompetitorBrands = current.includes(brand)
				? current.filter((item) => item !== brand)
				: [...current, brand];

			if (selectedCompetitorBrands.length === competitorBrandOptions.length) {
				return {
					...prev,
					selectedCompetitorBrands: [],
				};
			}

			return {
				...prev,
				selectedCompetitorBrands,
			};
		});
	};

	const selectedStateCount = filterObj.selectedStates.includes("All India")
		? stateOptions.length - 1
		: filterObj.selectedStates.length;

	return (
		<Paper
			elevation={0}
			sx={{
				position: "absolute",
				top: 12,
				left: 72,
				width: 360,
				maxWidth: "calc(100vw - 120px)",
				height: "calc(100vh - 165px)",
				overflowY: "auto",
				p: 2,
				borderRadius: 4,
				border: "1px solid rgba(34,68,58,0.12)",
				background: "rgba(255,255,255,0.94)",
				backdropFilter: "blur(14px)",
				boxShadow: "0 22px 46px rgba(48, 67, 61, 0.18)",
				zIndex: 1200,
				transition: "transform 220ms ease, opacity 220ms ease",
				transform: controlsOpen ? "translateX(0)" : "translateX(calc(-100% - 24px))",
				opacity: controlsOpen ? 1 : 0,
				pointerEvents: controlsOpen ? "auto" : "none",
			}}>
			<Stack spacing={2} sx={{ minHeight: "100%" }}>
				<Box display="flex" alignItems="flex-start" justifyContent="space-between" columnGap={1}>
					<Box>
						<Typography variant="h5" sx={{ fontWeight: 700, color: "#16342d" }}>
							{MAP_PANEL_TITLE}
						</Typography>
						<Typography sx={{ fontSize: 13, color: "#64736f", mt: 0.5 }}>{MAP_PANEL_SUBTITLE}</Typography>
					</Box>
				</Box>

				<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
					{summaryCards.map((card) => (
						<Chip
							key={card.label}
							label={`${card.label}: ${card.value}`}
							sx={{ background: "#edf3f1", color: "#24433b", fontWeight: 600, borderRadius: 2 }}
						/>
					))}
				</Stack>

				<Divider />

				<Box>
					<Typography sx={sectionTitleSx}>Quick Presets</Typography>
					<Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
						{PRESET_OPTIONS.map((option) => (
							<Button
								key={option.key}
								variant="outlined"
								size="small"
								disabled={isApplying}
								sx={controlButtonSx(false)}
								onClick={() => applyPreset(option.key)}>
								{option.label}
							</Button>
						))}
					</Stack>
				</Box>

				<Divider />

				<Box>
					<Typography sx={sectionTitleSx}>State Scope</Typography>
					<Stack direction="row" spacing={1} mt={1} mb={1} flexWrap="wrap" useFlexGap>
						<Button
							variant="outlined"
							size="small"
							sx={controlButtonSx(false)}
							onClick={() => setSelectedStates(["All India"])}>
							All India
						</Button>
						<Button variant="outlined" size="small" sx={controlButtonSx(false)} onClick={() => setSelectedStates([])}>
							Clear
						</Button>
					</Stack>
					<TextField
						fullWidth
						size="small"
						label="Search states"
						value={filterObj.stateQuery}
						onChange={(event) => setFilterObj((prev) => ({ ...prev, stateQuery: event.target.value }))}
					/>
					<Box
						sx={{
							mt: 1.5,
							maxHeight: 180,
							overflowY: "auto",
							pr: 0.5,
							border: "1px solid #dde7e2",
							borderRadius: 2,
							background: "#f9fbfa",
						}}>
						<FormGroup sx={{ p: 1 }}>
							{visibleStates.map((state) => (
								<FormControlLabel
									key={state}
									control={
										<Checkbox
											size="small"
											checked={filterObj.selectedStates.includes(state)}
											onChange={() => handleStateToggle(state)}
										/>
									}
									label={state}
								/>
							))}
						</FormGroup>
					</Box>
					<Typography sx={{ mt: 1, fontSize: 12, color: "#6a7a75" }}>Selected states: {selectedStateCount}</Typography>
				</Box>

				<Divider />

				<Box>
					<Typography sx={sectionTitleSx}>Region Subset</Typography>
					<Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
						{REGION_SUBSET_OPTIONS.map((option) => (
							<Button
								key={option.key}
								variant="outlined"
								size="small"
								sx={controlButtonSx(filterObj.regionSubset === option.key)}
								onClick={() => setFilterObj((prev) => ({ ...prev, regionSubset: option.key }))}>
								{option.label}
							</Button>
						))}
					</Stack>
				</Box>

				<Box>
					<Typography sx={sectionTitleSx}>Color Mode</Typography>
					<Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
						{COLOR_MODES.map((mode) => (
							<Button
								key={mode.key}
								variant="outlined"
								size="small"
								sx={controlButtonSx(filterObj.colorMode === mode.key)}
								onClick={() => setFilterObj((prev) => ({ ...prev, colorMode: mode.key }))}>
								{mode.label}
							</Button>
						))}
					</Stack>
				</Box>

				<FormControl fullWidth size="small">
					<InputLabel id="palette-label">Hue Palette</InputLabel>
					<Select
						labelId="palette-label"
						label="Hue Palette"
						value={filterObj.palette}
						onChange={(event) => setFilterObj((prev) => ({ ...prev, palette: event.target.value }))}>
						{HUE_PALETTES.map((option) => (
							<MenuItem key={option.key} value={option.key}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<Box>
					<Typography sx={sectionTitleSx}>Legend</Typography>
					<Box
						sx={{
							mt: 1,
							p: 1.25,
							border: "1px solid #dde7e2",
							borderRadius: 2,
							background: "#f9fbfa",
						}}>
						{activeColorMode.legendType === "note" && (
							<Typography sx={{ fontSize: 12, color: "#5d6b67", lineHeight: 1.45 }}>
								{activeColorMode.legendNote}
							</Typography>
						)}
						{activeColorMode.legendType === "scale" && (
							<Box>
								<Box
									sx={{
										height: 14,
										borderRadius: 1,
										border: "1px solid #d6ddd9",
										background: paletteGradient,
									}}
								/>
								<Box display="flex" justifyContent="space-between" mt={0.5}>
									<Typography sx={{ fontSize: 11, color: "#687672" }}>{activeColorMode.minLabel}</Typography>
									<Typography sx={{ fontSize: 11, color: "#687672" }}>{activeColorMode.legendTitle}</Typography>
									<Typography sx={{ fontSize: 11, color: "#687672" }}>{activeColorMode.maxLabel}</Typography>
								</Box>
							</Box>
						)}
						{activeColorMode.legendType === "steps" && (
							<Stack spacing={0.75}>
								{activeColorMode.steps.map((step) => (
									<Box key={step.label} display="flex" alignItems="center" columnGap={1}>
										<Box
											sx={{
												width: 12,
												height: 12,
												borderRadius: "3px",
												border: "1px solid #999",
												background: step.color,
											}}
										/>
										<Typography sx={{ fontSize: 12, color: "#5d6b67" }}>{step.label}</Typography>
									</Box>
								))}
							</Stack>
						)}
					</Box>
				</Box>

				<Divider />

				<Box>
					<Typography sx={sectionTitleSx}>Layer Visibility</Typography>
					<FormGroup sx={{ mt: 1 }}>
						{layerOptions.map((layer) => (
							<FormControlLabel
								key={layer.key}
								control={
									<Checkbox
										size="small"
										checked={filterObj.layers[layer.key]}
										onChange={() => handleLayerToggle(layer.key)}
									/>
								}
								label={layer.kindLabel ?? layer.label}
							/>
						))}
					</FormGroup>
				</Box>

				<Divider />

				<Box>
					<Typography sx={sectionTitleSx}>Point Layers</Typography>
					<Typography sx={{ mt: 1, fontSize: 12, color: "#65746f" }}>
						Own-store markers render from representative-point coordinates and remain split by store type.
					</Typography>
					<Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
						<Chip
							size="small"
							label={hasStoreLayersEnabled ? "Own stores enabled" : "Own stores hidden"}
							sx={{ background: hasStoreLayersEnabled ? "#e8f2ee" : "#eef2f1", color: "#26443b", fontWeight: 600 }}
						/>
						<Chip
							size="small"
							label={filterObj.layers.COMPETITOR_POINTS ? "Competitors enabled" : "Competitors hidden"}
							sx={{
								background: filterObj.layers.COMPETITOR_POINTS ? "#eef0fb" : "#eef2f1",
								color: "#26443b",
								fontWeight: 600,
							}}
						/>
					</Stack>
				</Box>

				<Divider />

				<Box>
					<Typography sx={sectionTitleSx}>Competitor Brands</Typography>
					<Stack direction="row" spacing={1} mt={1} mb={1} flexWrap="wrap" useFlexGap>
						<Button
							variant="outlined"
							size="small"
							sx={controlButtonSx(isAllCompetitorBrandsSelected)}
							onClick={() => setSelectedCompetitorBrands([])}>
							All Brands
						</Button>
						<Button
							variant="outlined"
							size="small"
							sx={controlButtonSx(isNoCompetitorBrandsSelected)}
							onClick={() => setSelectedCompetitorBrands([NONE_BRAND_SENTINEL])}>
							Clear All
						</Button>
					</Stack>
					<TextField
						fullWidth
						size="small"
						label="Search competitor brands"
						value={filterObj.competitorQuery}
						onChange={(event) => setFilterObj((prev) => ({ ...prev, competitorQuery: event.target.value }))}
					/>
					<Box
						sx={{
							mt: 1.5,
							maxHeight: 180,
							overflowY: "auto",
							pr: 0.5,
							border: "1px solid #dde7e2",
							borderRadius: 2,
							background: "#f9fbfa",
						}}>
						{visibleCompetitorBrands.length ? (
							<FormGroup sx={{ p: 1 }}>
								{visibleCompetitorBrands.map((brand) => (
									<FormControlLabel
										key={brand}
										control={
											<Checkbox
												size="small"
												checked={
													!isNoCompetitorBrandsSelected &&
													(isAllCompetitorBrandsSelected || filterObj.selectedCompetitorBrands.includes(brand))
												}
												onChange={() => handleCompetitorBrandToggle(brand)}
											/>
										}
										label={brand}
									/>
								))}
							</FormGroup>
						) : (
							<Box sx={{ p: 1.5 }}>
								<Typography sx={{ fontSize: 12, color: "#6a7a75" }}>
									No competitor brands loaded for the current state scope yet.
								</Typography>
							</Box>
						)}
					</Box>
					<Typography sx={{ mt: 1, fontSize: 12, color: "#6a7a75" }}>
						Active brand filters:{" "}
						{isAllCompetitorBrandsSelected
							? "All brands"
							: isNoCompetitorBrandsSelected
								? "No brands selected"
								: filterObj.selectedCompetitorBrands.length}
					</Typography>
				</Box>

				<Box
					sx={{
						position: "sticky",
						bottom: -16,
						pt: 1,
						pb: 1,
						mt: "auto",
						background: "linear-gradient(to top, rgba(255,255,255,0.98), rgba(255,255,255,0.82))",
						backdropFilter: "blur(8px)",
					}}>
					<Button
						fullWidth
						variant="contained"
						onClick={() => onApplyFilters()}
						disabled={isApplying || !hasPendingChanges}
						startIcon={isApplying ? <CircularProgress size={16} color="inherit" /> : null}
						sx={{
							height: 46,
							borderRadius: 999,
							textTransform: "none",
							fontWeight: 700,
							background: "#17362f",
							boxShadow: "0 16px 28px rgba(23, 54, 47, 0.22)",
							"&:hover": {
								background: "#102720",
							},
							"&.Mui-disabled": {
								background: "#c4cfca",
								color: "#f7f9f8",
							},
						}}>
						{isApplying ? "Applying map changes..." : hasPendingChanges ? "Apply Map Changes" : "Map Is Up To Date"}
					</Button>
				</Box>
			</Stack>
		</Paper>
	);
};

export default MapControls;
